import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, forgetOnThisBrowser, unlock, type Child, type GateState, type SessionInfo } from './api'
import { outbox } from './lib/outbox'
import { ErrorScreen, Loading } from './components/States'
import { Landing } from './screens/Landing'
import { Subscribe } from './screens/Subscribe'
import { Consent } from './screens/Consent'
import { ChildSetup } from './screens/ChildSetup'
import { Home } from './screens/Home'
import { InterestCheckIn } from './screens/InterestCheckIn'
import { SessionMap } from './screens/SessionMap'
import { Session } from './screens/Session'
import { Summary } from './screens/Summary'
import { Badges } from './screens/Badges'
import { TrophyRoom3D } from './screens/TrophyRoom3D'
import { Garden } from './screens/Garden'
import { WordMap } from './screens/WordMap'
import { WordUniverse } from './screens/WordUniverse'
import { WordUniverse3D } from './screens/WordUniverse3D'
import { Tree } from './screens/Tree'
import { Games } from './screens/Games'
import { Crossword } from './screens/Crossword'
import { MysteryWord } from './screens/MysteryWord'
import { Hangman } from './screens/Hangman'
import { BuddyScreen } from './screens/BuddyScreen'
import { Levels } from './screens/Levels'
import { AskForWord } from './screens/AskForWord'
import { Settings } from './screens/Settings'
import { StartingCheck } from './screens/StartingCheck'
import { TryIt } from './screens/TryIt'
import { ConsentLink } from './screens/ConsentLink'
import { SoundControls } from './components/SoundControls'
import { ChildBuddy, Mascot } from './components/Mascot'
import { rememberedScreenLanguage, rememberScreenLanguage, setScreenLanguage, t } from './lib/i18n'
import { parseBuddy } from './components/SproutBuddy'
import { PinPad } from './components/PinPad'
import { GrownUpGate } from './components/GrownUpGate'
import { ChildPinMaker } from './components/ChildPinMaker'
import { applyTheme, theme } from './theme'
import { DEFAULT_THEME } from './themes'

/**
 * Routing.
 *
 * The server decides where the app should be, through /api/state. The four
 * gates live there and nowhere else — if the front end worked the order out
 * for itself, the two copies would eventually disagree, and the one users hit
 * would be the wrong one.
 *
 * Starting a session is the one multi-step path: interest check-in when it is
 * due, then the map, then the questions. The check-in fires on the first
 * session and then monthly.
 */
type View =
  | { name: 'gate' }
  | { name: 'child_setup'; index: number }
  | { name: 'interests'; child: Child; session: SessionInfo }
  | { name: 'map'; child: Child; session: SessionInfo }
  | { name: 'session'; child: Child; session: SessionInfo }
  | { name: 'summary'; sessionId: string; child: Child }
  | { name: 'badges'; child: Child }
  | { name: 'trophies3d'; child: Child }
  | { name: 'garden'; child: Child }
  | { name: 'wordmap'; child: Child }
  | { name: 'universe'; child: Child }
  | { name: 'universe_flat'; child: Child }
  | { name: 'tree'; child: Child }
  | { name: 'games'; child: Child }
  | { name: 'crossword'; child: Child }
  | { name: 'mystery'; child: Child }
  | { name: 'hangman'; child: Child }
  | { name: 'buddy'; child: Child }
  | { name: 'levels'; child: Child }
  | { name: 'ask'; child: Child }
  | { name: 'settings' }
  | { name: 'starting_check'; child: Child }
  | { name: 'try' }

/**
 * Wraps every screen that belongs to a child, and only those.
 *
 * It carries the music and the sound buttons. Because it sits at the same
 * place in the tree for every child screen, React keeps one instance across
 * them — so the music carries on from the map into the session into the
 * summary without restarting, and stops the moment a grown-up's screen
 * replaces it.
 */
function ChildSpace({ child, children }: { child: Child; children: ReactNode }) {
  // Their own Sprout Buddy on every page while they are in (Sam, 18 Oct 2026).
  const buddy = useMemo(() => parseBuddy(child.avatar_ref), [child.avatar_ref])
  return (
    <ChildBuddy.Provider value={buddy}>
      {children}
      {/* Whose space this is, on every child screen (build list item 4,
          25 Oct 2026): their name and their own buddy, pinned top-right.
          Home sits top-left (ExitButton) and the sound buttons bottom-right. */}
      <div
        aria-label={`${child.display_name}'s space`}
        style={{
          position: 'fixed', top: 'max(12px, env(safe-area-inset-top, 0px))',
          right: 'max(12px, env(safe-area-inset-right, 0px))', zIndex: 50,
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '2px 12px 2px 4px', borderRadius: 999,
          background: theme.card, border: `1px solid ${theme.border}`,
          boxShadow: '0 1px 4px rgba(21,32,43,0.08)', pointerEvents: 'none',
        }}
      >
        <Mascot size={34} animate={false} label={`${child.display_name}'s buddy`} />
        <span style={{ fontSize: 15, fontWeight: 800, color: theme.text }}>{child.display_name}</span>
      </div>
      <SoundControls wantsMusic />
    </ChildBuddy.Provider>
  )
}

/** A link from a consent email: `?consent=TOKEN` or `?withdraw=TOKEN` (ConsentLink.tsx). */
function linkFromAddress(): { kind: 'consent' | 'withdraw'; token: string } | null {
  try {
    const q = new URLSearchParams(window.location.search)
    const consent = q.get('consent')
    if (consent) return { kind: 'consent', token: consent }
    const withdraw = q.get('withdraw')
    if (withdraw) return { kind: 'withdraw', token: withdraw }
  } catch { /* no address to read */ }
  return null
}

export function App() {
  const [link, setLink] = useState(linkFromAddress)
  const [state, setState] = useState<GateState | null>(null)
  const [view, setView] = useState<View>({ name: 'gate' })
  const [error, setError] = useState<unknown>(null)
  const [starting, setStarting] = useState(false)
  // A child on their way in who needs a PIN first: type it, or (no PIN yet,
  // or forgotten) fetch a grown-up and choose one. D-065.
  // "Add a child" from Home asks for the grown-up PIN first (Codex round 22).
  const [addingChild, setAddingChild] = useState(false)
  const [asking, setAsking] = useState<
    { child: Child; then: () => void; step: 'pin' | 'grown_up' | 'make' } | null>(null)
  // Whose colours the app is currently wearing.
  //
  // Kept in state rather than only in the theme module, because React needs a
  // reason to re-render when the palette changes — the module itself is a
  // plain object and changing it is invisible to React. See theme.ts.
  // Read by nothing directly. Its only job is to give React a reason to
  // re-render when the palette swaps underneath it — see theme.ts.
  const [, setThemeId] = useState<string>(DEFAULT_THEME)

  const wearTheme = useCallback((id: string | null | undefined) => {
    const next = id || DEFAULT_THEME
    applyTheme(next)
    setThemeId(next)
  }, [])

  const refresh = useCallback(async () => {
    setError(null)
    // Back at Home: stop sending any child's PIN ticket, and drop the
    // grown-up's. Whoever taps a card next proves who they are again.
    unlock.use(null)
    unlock.dropGrownUp()
    try {
      const next = await api.state()
      // Waiting answers go only to the family they belong to (lib/outbox).
      outbox.setFamily(next.signed_in ? next.parent?.family_tag ?? null : null)
      // Signed out from elsewhere (a lost tablet removed in Settings, or the
      // sign-in ran out): clear what this browser kept (Codex round 22).
      if (!next.signed_in) forgetOnThisBrowser(false)
      // The grown-up screens' language; the children's screens never call t().
      // Before sign-in, and for an account that has not chosen one, the
      // language picked on the front page of this browser (round 14).
      const chosen = next.parent?.grown_up_language
      if (chosen === 'zh-TW' || chosen === 'es') rememberScreenLanguage(chosen)
      setScreenLanguage(chosen || rememberedScreenLanguage(), next.parent?.home_language)
      setState(next)
      setView({ name: 'gate' })
      return next
    } catch (err) {
      setError(err)
      return null
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  // The server said "this needs a PIN" (a ticket ran out, say). Go Home,
  // where tapping the card asks for it, rather than showing an error.
  useEffect(() => {
    const onLocked = () => { void refresh() }
    window.addEventListener('ws-locked', onLocked)
    return () => window.removeEventListener('ws-locked', onLocked)
  }, [refresh])

  // The app wears the colours of whoever last used it.
  //
  // An earlier version reset to the default on Home, reasoning that Home is
  // shared between two children so there is no correct answer to whose
  // colours it should be in. That reasoning undid the feature: the picker is
  // ON Home, so choosing a colour applied it, then the refresh that followed
  // set the view back to the gate, this effect fired, saw no child, and put
  // it straight back to the default. The click appeared to do nothing.
  //
  // So: entering a child's part of the app wears that child's colours, and
  // nothing ever resets. Home keeps whatever was chosen last, which with two
  // children means it wears the colours of whoever was practising most
  // recently — a better answer than "nobody's" anyway.
  useEffect(() => {
    if ('child' in view) wearTheme(view.child.theme)
  }, [view, wearTheme])

  /**
   * Run `then` once this child is allowed in.
   *
   * With two or more children, every child needs their PIN (D-065). A child
   * with no PIN yet, which was Charles and Mark until 8 Oct 2026, is no
   * longer waved through: a grown-up helps them choose one. Once typed, a
   * PIN holds for two minutes (fifteen until 22 Oct 2026), so a child going
   * from their session to their garden is not asked twice, but a sibling who
   * picks up the tablet afterwards is.
   *
   * The server checks the ticket the PIN earned, so this is the friendly
   * half of the lock, not the lock itself.
   */
  const asChild = (child: Child, then: () => void) => {
    const active = (state?.children ?? []).filter((c) => c.status !== 'archived')
    if (active.length < 2 || unlock.isFresh(child.learner_id)) {
      unlock.use(child.learner_id)
      then()
      return
    }
    setAsking({ child, then, step: child.has_pin ? 'pin' : 'grown_up' })
  }

  const letIn = () => {
    if (!asking) return
    const { child, then } = asking
    setAsking(null)
    // Making a PIN needed a grown-up; their ticket must not stay behind for
    // the child who is about to use the app.
    unlock.dropGrownUp()
    unlock.use(child.learner_id)
    then()
  }

  const startSession = async (child: Child, more = false) => {
    if (starting) return
    setStarting(true)
    try {
      const session = more
        ? await api.practiseMore(child.learner_id)
        : await api.startSession(child.learner_id)
      if (session.cursor >= session.planned_count) {
        // Every question already answered. Show the summary, not a map with
        // nothing left on it.
        setView({ name: 'summary', sessionId: session.session_id, child })
        return
      }
      // A resumed session skips the check-in: it was already asked when the
      // session began, and asking again on the way back in would be a nag.
      if (more) {
        // Five more go straight to the questions: no map for five.
        setView({ name: 'session', child, session })
      } else if (session.interests_due && !session.resumed) {
        setView({ name: 'interests', child, session })
      } else {
        setView({ name: 'map', child, session })
      }
    } catch (err) {
      setError(err)
    } finally {
      setStarting(false)
    }
  }

  if (link) {
    return <ConsentLink kind={link.kind} token={link.token} onDone={() => {
      // The token comes out of the address bar, so a reload cannot press it again.
      window.history.replaceState(null, '', window.location.pathname)
      setLink(null)
      void refresh()
    }} />
  }
  if (error) return <ErrorScreen error={error} onRetry={() => void refresh()} />
  if (!state) return <Loading what="Starting Word Sprout" />
  // Between the PIN and the session map the server plans the session. It
  // used to leave Home on screen, looking stuck, for a second or more (Sam,
  // 8 Oct 2026). Now the buddy says what is happening.
  if (starting) return <Loading what="Getting your words ready" />

  // ---- views that sit on top of the gates
  switch (view.name) {
    case 'interests':
      return (
        <ChildSpace child={view.child}>
        <InterestCheckIn
          learnerId={view.child.learner_id}
          displayName={view.child.display_name}
          onDone={() => setView({ name: 'map', child: view.child, session: view.session })}
        />
        </ChildSpace>
      )
    case 'map':
      return (
        <ChildSpace child={view.child}>
        <SessionMap
          child={view.child}
          session={view.session}
          onStart={() => setView({ name: 'session', child: view.child, session: view.session })}
          onHome={() => void refresh()}
          onChangeInterests={() =>
            setView({ name: 'interests', child: view.child, session: view.session })}
        />
        </ChildSpace>
      )
    case 'session':
      return (
        <ChildSpace child={view.child}>
        <Session
          child={view.child}
          session={view.session}
          onLeave={() => void refresh()}
          onFinished={(sessionId) =>
            setView({ name: 'summary', sessionId, child: view.child })}
        />
        </ChildSpace>
      )
    case 'summary':
      return (
        <ChildSpace child={view.child}>
        <Summary
          sessionId={view.sessionId}
          onHome={() => void refresh()}
          onBadges={() => setView({ name: 'badges', child: view.child })}
          onGarden={() => setView({ name: 'garden', child: view.child })}
          onMore={() => void startSession(view.child, true)}
        />
        </ChildSpace>
      )
    case 'badges':
      return (
        <ChildSpace child={view.child}>
        <Badges
          learnerId={view.child.learner_id}
          onHome={() => void refresh()}
          onGarden={() => setView({ name: 'garden', child: view.child })}
          on3D={() => setView({ name: 'trophies3d', child: view.child })}
        />
        </ChildSpace>
      )
    case 'trophies3d':
      return (
        <ChildSpace child={view.child}>
          <TrophyRoom3D
            learnerId={view.child.learner_id}
            onHome={() => void refresh()}
            onFlat={() => setView({ name: 'badges', child: view.child })}
          />
        </ChildSpace>
      )
    case 'garden':
      return (
        <ChildSpace child={view.child}>
        <Garden
          learnerId={view.child.learner_id}
          onHome={() => void refresh()}
          onBadges={() => setView({ name: 'badges', child: view.child })}
          onWordMap={() => setView({ name: 'wordmap', child: view.child })}
        />
        </ChildSpace>
      )
    case 'wordmap':
      return (
        <ChildSpace child={view.child}>
        <WordMap
          learnerId={view.child.learner_id}
          onHome={() => void refresh()}
          onGarden={() => setView({ name: 'garden', child: view.child })}
        />
        </ChildSpace>
      )
    case 'universe':
      // The 3D solar system first (8 Oct 2026); the flat map is one tap away.
      return (
        <ChildSpace child={view.child}>
          <WordUniverse3D
            learnerId={view.child.learner_id}
            onHome={() => void refresh()}
            onFlat={() => setView({ name: 'universe_flat', child: view.child })}
          />
        </ChildSpace>
      )
    case 'universe_flat':
      return (
        <ChildSpace child={view.child}>
          <WordUniverse
            learnerId={view.child.learner_id}
            onHome={() => void refresh()}
            onGarden={() => setView({ name: 'garden', child: view.child })}
            on3D={() => setView({ name: 'universe', child: view.child })}
          />
        </ChildSpace>
      )
    case 'buddy':
      return (
        <ChildSpace child={view.child}>
          <BuddyScreen child={view.child} onHome={() => void refresh()} />
        </ChildSpace>
      )
    case 'tree':
      return (
        <ChildSpace child={view.child}>
          <Tree learnerId={view.child.learner_id} child={view.child} onHome={() => void refresh()} />
        </ChildSpace>
      )
    case 'games':
      return (
        <ChildSpace child={view.child}>
          <Games
            onHome={() => void refresh()}
            onCrossword={() => setView({ name: 'crossword', child: view.child })}
            onMystery={() => setView({ name: 'mystery', child: view.child })}
            onHangman={() => setView({ name: 'hangman', child: view.child })}
          />
        </ChildSpace>
      )
    case 'crossword':
      return (
        <ChildSpace child={view.child}>
          <Crossword
            learnerId={view.child.learner_id}
            onHome={() => void refresh()}
            onBack={() => setView({ name: 'games', child: view.child })}
          />
        </ChildSpace>
      )
    case 'mystery':
      return (
        <ChildSpace child={view.child}>
          <MysteryWord
            learnerId={view.child.learner_id}
            onHome={() => void refresh()}
            onBack={() => setView({ name: 'games', child: view.child })}
          />
        </ChildSpace>
      )
    case 'hangman':
      return (
        <ChildSpace child={view.child}>
          <Hangman
            learnerId={view.child.learner_id}
            onHome={() => void refresh()}
            onBack={() => setView({ name: 'games', child: view.child })}
          />
        </ChildSpace>
      )
    case 'levels':
      return (
        <ChildSpace child={view.child}>
        <Levels
          learnerId={view.child.learner_id}
          onHome={() => void refresh()}
          onGarden={() => setView({ name: 'garden', child: view.child })}
          onWordMap={() => setView({ name: 'wordmap', child: view.child })}
        />
        </ChildSpace>
      )
    case 'ask':
      return (
        <ChildSpace child={view.child}>
        <AskForWord
          learnerId={view.child.learner_id}
          onHome={() => void refresh()}
          onGarden={() => setView({ name: 'garden', child: view.child })}
        />
        </ChildSpace>
      )
    case 'settings':
      return <Settings onHome={() => void refresh()} device={state.parent?.device_label ?? null}
        onStartingCheck={(child) => setView({ name: 'starting_check', child })} />
    case 'try':
      return <TryIt onLeave={() => void refresh()} onSignUp={() => void refresh()} />
    case 'starting_check':
      // Back to Settings afterwards. The check dropped the grown-up's ticket
      // for the child's turn, so Settings asks for the PIN again, unless the
      // grown-up has just typed it to see the result.
      return <StartingCheck child={view.child} onDone={() => { unlock.use(null); setView({ name: 'settings' }) }} />
    case 'child_setup':
      return (
        <ChildSetup
          index={view.index}
          onSaved={() => setView({ name: 'child_setup', index: view.index + 1 })}
          onFinish={() => void refresh()}
        />
      )
    default:
      break
  }

  // ---- the four gates, in the order the server gives them
  switch (state.next_screen) {
    case 'signin':
      return <Landing gate={state} onDone={() => void refresh()}
        onTry={state.try_on_device ? () => setView({ name: 'try' }) : undefined} />
    case 'consent':
      return <Consent gate={state} onDone={() => void refresh()}
        onSignOut={async () => { await api.signOut(); void refresh() }}
        onTry={() => setView({ name: 'try' })} />
    case 'subscribe':
      return (
        <Subscribe
          onDone={() => void refresh()}
          onSignOut={async () => { await api.signOut(); void refresh() }}
        />
      )
    case 'child_setup':
      return (
        <ChildSetup
          index={(state.children?.length ?? 0) + 1}
          onSaved={() => void refresh()}
          onFinish={() => void refresh()}
        />
      )
    case 'home':
      return (
        <>
        {asking?.step === 'pin' ? (
          <PinPad
            child={asking.child}
            onCancel={() => setAsking(null)}
            onUnlocked={letIn}
            onForgot={() => setAsking({ ...asking, step: 'grown_up' })}
          />
        ) : null}
        {asking?.step === 'grown_up' ? (
          <GrownUpGate
            why={asking.child.has_pin
              ? t('{name} forgot their PIN. A grown-up can help them choose a new one.', { name: asking.child.display_name })
              : t('{name} needs a PIN first. A grown-up helps them choose one.', { name: asking.child.display_name })}

            onCancel={() => { unlock.dropGrownUp(); setAsking(null) }}
            onPass={() => setAsking((a) => (a ? { ...a, step: 'make' } : a))}
          />
        ) : null}
        {addingChild ? (
          <GrownUpGate
            why={t('Only a grown-up can add a child.')}
            onCancel={() => { unlock.dropGrownUp(); setAddingChild(false) }}
            onPass={() => {
              setAddingChild(false)
              setView({ name: 'child_setup', index: (state.children?.length ?? 0) + 1 })
            }}
          />
        ) : null}
        {asking?.step === 'make' ? (
          <ChildPinMaker
            child={asking.child}
            onCancel={() => { unlock.dropGrownUp(); setAsking(null) }}
            onDone={letIn}
          />
        ) : null}
        <Home
          onStart={(child) => asChild(child, () => void startSession(child))}
          onAddChild={state.parent?.device_label ? undefined : () => setAddingChild(true)}
          onSettings={() => setView({ name: 'settings' })}
          onSignOut={async () => { await api.signOut(); void refresh() }}
          onBadges={(child) => asChild(child, () => setView({ name: 'badges', child }))}
          onGarden={(child) => asChild(child, () => setView({ name: 'garden', child }))}
          onWordMap={(child) => asChild(child, () => setView({ name: 'wordmap', child }))}
          onUniverse={(child) => asChild(child, () => setView({ name: 'universe', child }))}
          onTree={(child) => asChild(child, () => setView({ name: 'tree', child }))}
          onGames={(child) => asChild(child, () => setView({ name: 'games', child }))}
          onBuddy={(child) => asChild(child, () => setView({ name: 'buddy', child }))}
          onLevels={(child) => asChild(child, () => setView({ name: 'levels', child }))}
          onAsk={(child) => asChild(child, () => setView({ name: 'ask', child }))}
          onGuard={asChild}
          onPickTheme={(child, id) => {
            // Change first, save second, and do NOT refresh.
            //
            // The child has already seen it work; a failed save is not worth
            // a dialog in front of a seven-year-old. And a refresh here would
            // rebuild the whole screen to show a change that has already
            // happened — which is what was wiping the choice out.
            wearTheme(id)
            void api.setTheme(child.learner_id, id).catch(() => undefined)
          }}
        />
        </>
      )
    default:
      return <Loading />
  }
}
